import { useParams } from 'react-router';

import {
  WitnessCompanyDetailForm,
  WitnessCompanyDetailResponse,
  NoharaRepresentative,
} from '../../types/company.types';
import { DepartmentContact } from '../../types/table/company.types';
import { validateWitnessCompanyRegistrationForm } from '../../utils/validations/witness-validation';
import {
  mappingEditWitnessCompanyData,
  CreateWitnessCompanyRequest,
} from '../../utils/mappers/witness-company-form-mapper';
import { companiesService } from '../../services/companies.service';
import {
  useCompanyDetailFormBase,
  CompanyDetailFormConfig,
} from '../common/use-company-detail-form-base';

const initialFormData: WitnessCompanyDetailForm = {
  companyType: '立会会社',
  officialName: '',
  tel: '',
  fax: '',
  contactPerson: '',
  contactTel: '',
  representatives: [
    {
      department: '',
      salesPerson: {
        id: '',
        firstName: '',
        lastName: '',
        code: '',
      },
    },
  ] as NoharaRepresentative[],
  isCommonCement: false,
};

export const useWitnessCompanyDetailForm = () => {
  const { witnessCompanyId } = useParams<{ witnessCompanyId: string }>();

  const config: CompanyDetailFormConfig<
    WitnessCompanyDetailForm,
    WitnessCompanyDetailResponse,
    CreateWitnessCompanyRequest
  > = {
    companyId: witnessCompanyId,
    initialFormData,
    validateForm: validateWitnessCompanyRegistrationForm,
    fetchCompanyById: (id: string) => companiesService.getWitnessCompanyById(id),
    updateCompanyById: (id: string, data: CreateWitnessCompanyRequest) =>
      companiesService.updateWitnessCompanyById(id, data),
    mapApiResponseToFormData: (apiData, findDepartmentIdByCode) => ({
      companyType: '立会会社',
      officialName: apiData.name || '',
      tel: apiData.tel || '',
      fax: apiData.fax || '',
      contactPerson: apiData.contactPerson || '',
      contactTel: apiData.contactTel || '',
      canUpdate: apiData.canUpdate || false,
      representatives:
        apiData.departmentContactLists?.map((contact: DepartmentContact, index: number) => {
          const department = findDepartmentIdByCode(contact.departmentCode);
          const departmentId = department ? department : (contact.departmentId ?? '');
          const staffId = {
            id: contact.saleStaff.id,
            firstName: contact.saleStaff.firstName,
            lastName: contact.saleStaff.lastName,
            code: contact.contactPersonCode.toString(),
          };
          return {
            department: departmentId,
            departmentCode: contact.departmentCode.toString(),
            departmentName: contact.departmentName,
            canView: contact.canView,
            salesPerson: staffId,
            canUpdate: contact.canUpdate,
            originalIndex: index,
          };
        }) ||
        ([
          {
            department: '',
            salesPerson: {
              id: '',
              firstName: '',
              lastName: '',
              code: '',
            },
          },
        ] as NoharaRepresentative[]),
      isCommonCement: apiData.isCommonCement || false,
    }),
    mapFormDataToApiRequest: mappingEditWitnessCompanyData,
    getDepartmentContactLists: (apiData) => apiData.departmentContactLists,
  };

  const result = useCompanyDetailFormBase(config);

  return {
    ...result,
    // Rename companyId to witnessCompanyId for backward compatibility
    witnessCompanyId: result.companyId,
  };
};
